import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Box,
  TextField,
  FormControl,
  Grid,
  Autocomplete,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import Locations from "examples/ReuseFunctions/Locations";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Enter a valid phone number")
    .required("Phone number is required"),
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Area is required"),
  address: Yup.string().required("Address is required"),
  skill_set: Yup.string().required("Skills are required"),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      "Invalid email format"
    )
    .typeError("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password is too short - should be 6 chars minimum.")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm Password is required"),
});

const initialValues = {
  name: "",
  email: "",
  phone: "",
  password: "",
  state_id: "",
  city_id: "",
  region_id: "",
  address: "",
  skill_set: "",
  confirm_password: "",
};
interface Roles {
  role_id: number;
  role_name: string;
}
interface Customers {
  division_name: string;
  division_id: string;
  customer_name: string;
  branch_code: string;
}
interface RoleType {
  role_id: number;
  role_name: string;
}

export interface AddEmployeeProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When set (e.g. from customer Members overlay), that customer's divisions are pre-selected */
  initialCustomerId?: number;
}

const AddEmployee = ({
  embedded,
  onSuccess,
  onCancel,
  initialCustomerId,
}: AddEmployeeProps = {}) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState<Roles[]>([]);
  const [customers, setCustomers] = useState<Customers[]>([]);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });

      return Array.isArray(response?.data?.list) ? response.data.list : [];
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
      return [];
    }
  }, [fetchError]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await axios.get("/roles/all");
        const allRoles = rolesData?.data?.list || [];
        const filteredRoles = allRoles
          .filter(
            (role: RoleType) =>
              !["admin", "superadmin", "customer"].includes(
                role.role_name.toLowerCase()
              )
          )
          // 2️⃣ Capitalize role names
          .map((role: RoleType) => ({
            ...role,
            role_name:
              role.role_name.charAt(0).toUpperCase() + role.role_name.slice(1),
          }));

        setRoles(filteredRoles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);
  const fetchDivisions = async () => {
    try {
      const res = await axios.get("/divisions/all");
      const list = res.data.list || [];
      setCustomers(list);
    } catch (err) {
      fetchError("Unable to fetch divisions");
      throw err;
    }
  };

  useEffect(() => {
    if (initialCustomerId) {
      axios
        .get(`/divisions/customer/${initialCustomerId}`)
        .then((res) => {
          const divisions = res?.data?.divisions || [];
          setCompanyIds(divisions.map((d: { division_id: number }) => String(d.division_id)));
        })
        .catch(() => {});
    }
  }, [initialCustomerId]);

  const content = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }) }}>
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Member
        </SoftTypography>
      )}
      <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            const { state_id, city_id, confirm_password, ...rest } = values;
            try {
              await axios.post("/employee/add-employee", {
                ...rest,
                role_id: selectedRoles.map((r) => r.role_id),
                division_id: selectedRoles.some(
                  (r) => r.role_id === 7 || r.role_id === 3
                )
                  ? companyIds
                  : [],
              });
              fetchSuccess("Member created successfully");
              if (embedded) {
                onSuccess?.();
              } else {
                navigate("/employees-list");
              }
            } catch (err) {
              if (err instanceof AxiosError && err.response) {
                fetchError(err.response.data.error);
              }
            }
          }}
        >
          {({ errors, touched, handleChange, values, setFieldValue }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Roles
                    </SoftTypography>
                    <Autocomplete
                      multiple
                      options={roles}
                      getOptionLabel={(option) => option.role_name || ""}
                      value={selectedRoles}
                      onChange={(event, newValue) => {
                        setSelectedRoles(newValue);
                        const hasBranchHead = newValue.some(
                          (r) => r.role_id === 7 || r.role_id === 3
                        );

                        if (hasBranchHead) {
                          fetchDivisions();
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" />
                      )}
                    />
                  </FormControl>
                </Grid>
                {selectedRoles.some(
                  (r) => r.role_id === 7 || r.role_id === 3
                ) && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Select Divisions
                      </SoftTypography>

                      <Autocomplete
                        multiple
                        options={customers}
                        getOptionLabel={(option) =>
                          `${option.division_name} - ${option.customer_name}-${option.branch_code}`
                        }
                        value={customers.filter((c) =>
                          companyIds.includes(c.division_id)
                        )}
                        onChange={(event, newValue) =>
                          setCompanyIds(newValue.map((c) => c.division_id))
                        }
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                      />
                      {selectedRoles.some(
                        (r) => r.role_name?.toLowerCase() === "supervisor"
                      ) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#9e9e9e",
                            display: "block",
                            mt: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          If the supervisor is to be assigned to specific divisions, please select the relevant divisions. Otherwise, this field may be left blank.
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Name
                    </SoftTypography>
                    <TextField
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.name && touched.name}
                      helperText={touched.name && errors.name}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Phone Number
                    </SoftTypography>
                    <TextField
                      name="phone"
                      value={values.phone}
                      inputProps={{ maxLength: 10 }}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.phone && touched.phone}
                      helperText={touched.phone && errors.phone}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Email
                    </SoftTypography>
                    <TextField
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.email && touched.email}
                      helperText={touched.email && errors.email}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Password
                    </SoftTypography>
                    <TextField
                      className="view-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.password && touched.password}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Confirm Password
                    </SoftTypography>
                    <TextField
                      className="view-password"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={values.confirm_password}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={
                        !!errors.confirm_password && touched.confirm_password
                      }
                      helperText={
                        touched.confirm_password && errors.confirm_password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      State
                    </SoftTypography>
                    <Autocomplete
                      id="state-autocomplete"
                      options={states ?? []}
                      getOptionLabel={(option) => option.state_name || ""}
                      value={
                        (states as unknown as any[])?.find(
                          (state) => state.state_id === values.state_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue("city_id", "");
                        setFieldValue("region_id", "");
                        setFieldValue(
                          "state_id",
                          newValue ? newValue.state_id : ""
                        );
                        setSelectedState(newValue ? newValue.state_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="state_id"
                          variant="outlined"
                          error={!!errors.state_id && touched.state_id}
                          helperText={touched.state_id && errors.state_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      City
                    </SoftTypography>
                    <Autocomplete
                      id="city-autocomplete"
                      options={cities ?? []}
                      getOptionLabel={(option) => option.city_name || ""}
                      value={
                        (cities as unknown as any[])?.find(
                          (city: { city_id: string }) =>
                            city.city_id === values.city_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue("region_id", "");
                        setFieldValue(
                          "city_id",
                          newValue ? newValue.city_id : ""
                        );
                        setSelectedCity(newValue ? newValue.city_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="city_id"
                          variant="outlined"
                          error={!!errors.city_id && touched.city_id}
                          helperText={touched.city_id && errors.city_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Area
                    </SoftTypography>
                    <Autocomplete
                      id="region-autocomplete"
                      options={areas ?? []}
                      getOptionLabel={(option) => option.region_name || ""}
                      value={
                        (areas as unknown as any[])?.find(
                          (city: { region_id: string }) =>
                            city.region_id === values.region_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "region_id",
                          newValue ? newValue.region_id : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="region_id"
                          variant="outlined"
                          error={!!errors.region_id && touched.region_id}
                          helperText={touched.region_id && errors.region_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Address
                    </SoftTypography>
                    <TextField
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.address && touched.address}
                      helperText={touched.address && errors.address}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Skills
                    </SoftTypography>
                    <TextField
                      name="skill_set"
                      value={values.skill_set}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.skill_set && touched.skill_set}
                      helperText={touched.skill_set && errors.skill_set}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item>
                    <SoftButton
                      className="success-btn"
                      variant="gradient"
                      color="success"
                      type="submit"
                    >
                      Save
                    </SoftButton>
                  </Grid>
                  <Grid item>
                    <SoftButton
                      className="cancel-btn"
                      variant="gradient"
                      color="warning"
                      onClick={() => {
                        if (embedded) {
                          onCancel?.();
                        } else {
                          navigate("/employees-list");
                        }
                      }}
                    >
                      Cancel
                    </SoftButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
    </Box>
  );

  if (embedded) {
    return content;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AddEmployee;
